import type { DomainAssessment } from '@lizrd/contracts';
import { Modal } from './ui/modal';
import { scoreBand } from '../lib/graph-visuals';

/**
 * The utilization score as a 1–5 star rating (5 best). Higher means the money you
 * spend maps to capacity you actually use. `size="lg"` is the Explorer-overview
 * tile; the compact default is for the Domains list.
 */
export function UtilizationScore({
  score,
  size = 'sm',
}: {
  score: number | null;
  size?: 'sm' | 'lg';
}) {
  if (score == null) {
    return <span className="text-muted">{size === 'lg' ? 'Not enough data' : '—'}</span>;
  }
  const { tone } = scoreBand(score);
  const starSize = size === 'lg' ? 'text-2xl' : 'text-base';
  return (
    <span
      className={`inline-flex items-center gap-0.5 leading-none ${starSize}`}
      role="img"
      aria-label={`${score} out of 5`}
      title={`${score} / 5`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} aria-hidden className={i <= score ? tone : 'text-muted/30'}>
          {i <= score ? '★' : '☆'}
        </span>
      ))}
    </span>
  );
}

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * Explains, roughly, how the utilization score is calculated — shown when the
 * score is clicked on the Explorer overview.
 */
export function UtilizationScoreModal({
  assessment,
  onClose,
}: {
  assessment: DomainAssessment;
  onClose: () => void;
}) {
  const score = assessment.utilizationScore;
  const band = score != null ? scoreBand(score) : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <h3 className="font-display text-base font-bold text-bone">Utilization Score</h3>
          {score != null && <UtilizationScore score={score} />}
          {band && <span className={`text-sm font-semibold ${band.tone}`}>{band.label}</span>}
        </div>
      }
    >
      <div className="space-y-4 text-sm leading-relaxed">
        <p className="text-haze">
          A single measure of <span className="text-bone">how well this domain is utilized</span> —
          how much of the capacity you pay for is actually in use, rated 1–5. Higher is better; a
          low score means you are likely paying for idle headroom.
        </p>

        <div className="space-y-2">
          <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
            How it's calculated
          </div>
          <ul className="space-y-1.5 text-xs text-haze">
            <li className="flex gap-2">
              <span className="text-iris">•</span>
              <span>
                For each resource we can measure, we compare its usage (the higher of CPU or memory)
                to a healthy target of <span className="text-bone">~65%</span>. At or above target
                scores full marks; idle scores low.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-iris">•</span>
              <span>
                Each resource is weighted by its <span className="text-bone">monthly cost</span>, so
                an expensive idle resource moves the score far more than a cheap one.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-iris">•</span>
              <span>
                We average across resources that have a utilization (compute, databases, caches).
                Resources with no utilization by design — NAT gateways, load balancers, storage,
                Lambda — are <strong className="text-bone">tracked by traffic and state</strong>{' '}
                instead (and surfaced as recommendations), so they don&apos;t distort the score or
                its coverage.
              </span>
            </li>
          </ul>
        </div>

        <dl className="grid grid-cols-2 gap-3 border-t border-hairline pt-3 text-center sm:grid-cols-4">
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted">Resources scored</dt>
            <dd className="font-display text-lg font-bold text-bone">
              {assessment.assessedResources}
            </dd>
          </div>
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted">Spend covered</dt>
            <dd className="font-display text-lg font-bold text-bone">{pct(assessment.coverage)}</dd>
          </div>
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted">
              Tracked by traffic
            </dt>
            <dd className="font-display text-lg font-bold text-bone">
              {assessment.trackedResources}
            </dd>
          </div>
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted">
              Target Utilization
            </dt>
            <dd className="font-display text-lg font-bold text-bone">65%</dd>
          </div>
        </dl>
        {assessment.coverage < 0.5 && assessment.assessedResources > 0 && (
          <p className="text-[0.7rem] text-muted">
            Note: only {pct(assessment.coverage)} of your scoreable spend has load metrics, so this
            score reflects that slice of the domain.
          </p>
        )}
      </div>
    </Modal>
  );
}
